import React, { memo, useCallback, useMemo } from 'react';
import { LeftOutlined, MinusOutlined, RightOutlined, CloseOutlined } from '@ant-design/icons';
import { Calendar, ConfigProvider } from 'antd';
import localeEn from 'antd/locale/en_US';
import localeJp from 'antd/locale/ja_JP';
import localeKo from 'antd/locale/ko_KR';
import localeVn from 'antd/locale/vi_VN';
import localeZhCn from 'antd/locale/zh_CN';
import localeZhTw from 'antd/locale/zh_TW';
import localeTh from 'antd/locale/th_TH';
import localeId from 'antd/locale/id_ID';
import dayjs, { Dayjs, ManipulateType } from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/ja';
import 'dayjs/locale/ko';
import 'dayjs/locale/vi';
import 'dayjs/locale/zh-cn';
import 'dayjs/locale/zh-tw';
import 'dayjs/locale/th';
import 'dayjs/locale/id';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';

import icTriangleOutline from '../../assets/imgs/icTriangleOutline.svg?react';
import useLocalizedTime from '../../hooks/useLocalizedTime';
import { calReservationAvailableEndAt } from '../../utils';
import './styles.css';
import styles from './styles.module.css';
import { Holidays, TicketInfo } from 'types';

type CustomCalendarProps = {
    reservationTicket?: TicketInfo;
    seatsInfo?: {
        availableDates: Set<unknown>;
        fewDates: Set<unknown>;
        fullDates: Set<unknown>;
    };
    holidays?: Holidays[];
    selectedTime: Dayjs;
    onSelectTime?: (date: Dayjs) => void;
    onDateClick?: (date: Dayjs) => Promise<void>;
};

const CustomCalendar: React.FC<CustomCalendarProps> = ({
    reservationTicket,
    seatsInfo,
    holidays,
    selectedTime,
    onSelectTime = () => {},
    onDateClick = () => {},
}) => {
    const { t } = useTranslation();
    const { formatTime } = useLocalizedTime();
    const { reservationAvailablePeriod, reservationAvailablePeriodUnit } = reservationTicket ?? {};

    // 言語の変更を監視し、Ant Designのロケールを設定します。
    const { antdLocale, localeFormatType } = useMemo(() => {
        const localeMapping = {
            ja: localeJp,
            en: localeEn,
            vi: localeVn,
            ko: localeKo,
            'zh-TW': localeZhTw,
            zh: localeZhCn,
            th: localeTh,
            id: localeId,
        };
        type LocaleKey = keyof typeof localeMapping;
        const locale = i18n.language as LocaleKey;
        const antdLocale = localeMapping[locale];
        const localeFormatType = i18n.language === 'ja' ? 'YYYY年MM月' : 'MMMM YYYY';
        return {
            antdLocale,
            localeFormatType,
        };
    }, []);

    // カレンダーのヘッダーをカスタムする
    const customHeader = ({ value, onChange }: { value: Dayjs; onChange: (day: Dayjs) => void }) => {
        const currentDateTime = dayjs();
        const selectedDateTime = value.clone();
        const selectedMonth = selectedDateTime.month();
        const dateOneYearLater = currentDateTime.add(1, 'year');
        const isPrevButtonDisabled = currentDateTime.isSame(selectedDateTime, 'month');
        const reservationAvailableEndAt = calReservationAvailableEndAt(
            currentDateTime,
            reservationAvailablePeriod,
            reservationAvailablePeriodUnit as ManipulateType,
        );
        const isNotStartSale = selectedDateTime.add(1, 'month').isAfter(reservationAvailableEndAt, 'month');
        const isNextButtonDisabled =
            selectedDateTime.add(1, 'month').isAfter(dateOneYearLater, 'month') || isNotStartSale;

        //前の月を選択したときの処理
        const handlePreClick = () => {
            if (isPrevButtonDisabled) {
                return;
            }
            const preMonth = selectedDateTime.month(selectedMonth - 1);
            onChange(preMonth);
        };

        //次の月を選択したときの処理
        const handleNextClick = () => {
            if (isNextButtonDisabled) {
                return;
            }
            const nextMonth = selectedDateTime.month(selectedMonth + 1);
            onChange(nextMonth);
        };

        return (
            <div className={styles.calendarHeader}>
                <button className={styles.changeMonthBtn} onClick={handlePreClick}>
                    <LeftOutlined
                        className={`${styles.changeMonthIcon} ${isPrevButtonDisabled ? styles.disabled : ''}`}
                    />
                </button>
                <p className={styles.currentMonth}>{formatTime(selectedDateTime.format(), localeFormatType)}</p>
                <button className={styles.changeMonthBtn} onClick={handleNextClick}>
                    <RightOutlined
                        className={`${styles.changeMonthIcon} ${isNextButtonDisabled ? styles.disabled : ''}`}
                    />
                </button>
            </div>
        );
    };

    // カレンダー内の各セル（日付）のコンテンツをカスタムする
    const cellRender = (current: Dayjs) => {
        const displayColor = () => {
            const dayOfWeek = current.format('dddd');
            const month = current.month();
            if (month !== selectedTime.month() || isDisabledDate(current)) return;
            if (holidays?.some((holiday) => dayjs(holiday?.holidayOn).isSame(dayjs(current), 'day'))) {
                return styles.sunday;
            }

            switch (dayOfWeek) {
                case 'Sunday':
                    return styles.sunday;
                case 'Saturday':
                    return styles.saturday;
                default:
                    return;
            }
        };
        return (
            <div className={`${styles.dateContainer} dateContainer`} onClick={() => void onDateClick(current)}>
                <div className={`${styles.dateContent} dateContent`}>
                    <span className={`${styles.currentDate} ${displayColor()}`}>{current.date()}</span>
                    <span className={styles.icon}>{displayIcon(current)}</span>
                </div>
            </div>
        );
    };

    //席の情報に基づいてアイコンを決定
    const displayIcon = (value: Dayjs) => {
        const month = value.month();
        const formattedDate = value.format('YYYY-MM-DD');
        if (month !== selectedTime.month() || isDisabledDate(value)) return;
        const { availableDates, fewDates, fullDates } = seatsInfo ?? {};

        if (availableDates?.has(formattedDate)) {
            return <span className={styles.circle}></span>;
        } else if (fewDates?.has(formattedDate)) {
            return <TriangleOutlined />;
        } else if (fullDates?.has(formattedDate)) {
            return <CloseOutlined className={styles.close} />;
        } else {
            return <MinusOutlined className={styles.minus} />;
        }
    };

    const isDisabledDate = (date: Dayjs) => {
        const currentDate = dayjs();
        const endOfMonthNextYear = currentDate.add(1, 'year').endOf('month');
        const isAfterEndOfMonthNextYear = date.isAfter(endOfMonthNextYear, 'date');
        const reservationAvailableEndAt = calReservationAvailableEndAt(
            currentDate,
            reservationAvailablePeriod,
            reservationAvailablePeriodUnit as ManipulateType,
        );

        const isNotStartSale = date.isAfter(reservationAvailableEndAt, 'day');
        return date.isBefore(currentDate, 'date') || isAfterEndOfMonthNextYear || isNotStartSale;
    };

    const handleDateChange = useCallback(
        (dateTime: Dayjs) => {
            const currentDate = dayjs();
            const endOfMonthNextYear = currentDate.add(1, 'year').endOf('month');
            const isAfterEndOfMonthNextYear = dateTime.isAfter(endOfMonthNextYear, 'day');
            const { reservationAvailablePeriod, reservationAvailablePeriodUnit } = reservationTicket ?? {};
            const reservationAvailableEndAt = calReservationAvailableEndAt(
                currentDate,
                reservationAvailablePeriod,
                reservationAvailablePeriodUnit as ManipulateType,
            );

            const isNotStartSale = dateTime.isAfter(reservationAvailableEndAt, 'day');
            if (isAfterEndOfMonthNextYear) {
                onSelectTime(endOfMonthNextYear);
            } else if (isNotStartSale && reservationAvailableEndAt) {
                onSelectTime(reservationAvailableEndAt);
            } else if (dateTime.isBefore(currentDate, 'day')) {
                onSelectTime(currentDate);
            } else {
                onSelectTime(dateTime);
            }
        },
        [reservationTicket],
    );

    return (
        <ConfigProvider
            locale={antdLocale}
            theme={{
                token: {
                    colorBgContainer: 'var(--background-color)',
                },
            }}
        >
            <div className={styles.wrapperContainer}>
                {/* <span className={styles.label}>{t('reservation.calendar-label')}</span> */}
                <Calendar
                    value={selectedTime}
                    headerRender={customHeader}
                    fullCellRender={cellRender}
                    onChange={handleDateChange}
                    disabledDate={isDisabledDate}
                    defaultValue={selectedTime}
                />
                <div className={styles.note}>
                    <div>
                        <span className={styles.circle}></span>
                        <span className={styles.noteText}>{t('reservation.reservation-amount.plenty')}</span>
                    </div>
                    <div>
                        <TriangleOutlined />
                        <span className={styles.noteText}>{t('reservation.reservation-amount.few')}</span>
                    </div>
                    <div>
                        <CloseOutlined className={styles.close} />
                        <span className={styles.noteText}>{t('reservation.reservation-amount.zero')}</span>
                    </div>
                    <div>
                        <MinusOutlined className={styles.minus} />
                        <span className={styles.noteText}>{t('reservation.reservation-amount.not-available')}</span>
                    </div>
                </div>
            </div>
        </ConfigProvider>
    );
};

function TriangleOutlined() {
    return <div>{React.createElement(icTriangleOutline, { className: styles.triangle })}</div>;
}
export default memo(CustomCalendar);
